import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { AuthUser, BusinessSummary, UserAccountProps } from '../utils/type';
import { ROLE } from '../Role';
import { useFetchUserBySessionId } from '../hook/useQueryData';
import { ACCOUNT_STATUS } from '../utils/enum';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => void;
  isAuthenticated: boolean;
  needsPhone: boolean;
  isVerifyUser: boolean;
  userId: string | null;
  role: string;
  profilePicture: string | null;
  subscriptionPlan: string | null;
  customerAccount: UserAccountProps | null;
  userAccounts: AuthUser['userAccount'] | null;
  firstName: string | null;
  lastName: string | null;
  hasPermissions: () => boolean;
  customerAccountStatus:
    | 'active'
    | 'inactive'
    | 'suspended'
    | 'deactivated'
    | 'pending_deletion'
    | 'deleted'
    | null;
  customerAccountId: string | null;
  providerAccountStatus:
    | 'active'
    | 'inactive'
    | 'suspended'
    | 'deactivated'
    | 'pending_deletion'
    | 'deleted'
    | null;
  adminAccountStatus:
    | 'active'
    | 'inactive'
    | 'suspended'
    | 'deactivated'
    | 'pending_deletion'
    | 'deleted'
    | null;

  /** Multi-business support (Phase 1) — per approved_architecture.md §4.3 */
  businessIds: string[];
  activeBusinessId: string | null;
  businesses: BusinessSummary[];
  /**
   * Validates that a given businessId belongs to the authenticated user.
   * Returns true iff businessId ∈ JWT businessIds[].
   * This is the frontend equivalent of requireBusinessScope.
   */
  isBusinessOwned: (businessId: string) => boolean;
}

const VALID_ACCESS_ROLES = [
  ROLE.CUSTOMER.toLocaleLowerCase(),
  ROLE.PROVIDER.toLocaleLowerCase(),
];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProviderNew = ({ children }: { children: ReactNode }) => {
  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchUserBySessionId();

  const isAuthenticated = !!user;
  const userId = user?.userId ?? null;
  const role = user?.role ?? null;
  const isVerifyUser = user?.isVerified ?? false;
  const subscriptionPlan = user?.membership ?? null;
  const profilePicture = user?.profilePicture ?? null;

  const mobile = user?.mobile ?? user?.phone ?? user?.phoneNumber ?? null;
  const needsPhone = isAuthenticated && !mobile?.trim();

  const userAccounts = user?.userAccount ?? null;
  const customerAccount = userAccounts?.customerAccount ?? null;

  const firstName = user?.firstName ?? null;
  const lastName = user?.lastName ?? null;

  const customerAccountStatus = userAccounts?.customerAccount?.state ?? null;
  const customerAccountId = userAccounts?.customerAccount?.accountId ?? null;

  const providerAccountStatus = userAccounts?.providerAccount?.state ?? null;
  const adminAccountStatus = userAccounts?.adminAccount?.state ?? null;

  // Multi-business support (Phase 1) — per approved_architecture.md §4.3
  const businessIds = useMemo(
    () => user?.businessIds ?? [],
    [user?.businessIds]
  );
  const activeBusinessId = user?.activeBusinessId ?? null;
  const businesses = user?.businesses ?? [];

  const isBusinessOwned = useCallback(
    (businessId: string): boolean => {
      return businessIds.includes(businessId);
    },
    [businessIds]
  );

  const hasValidAccessRole = useMemo(() => {
    if (!role) return false;
    return VALID_ACCESS_ROLES.includes(role.toLowerCase());
  }, [role]);

  const hasPermissions = useCallback((): boolean => {
    if (!isAuthenticated || !hasValidAccessRole) {
      return false;
    }

    const allowedStatuses = [ACCOUNT_STATUS.ACTIVE];

    // Check the account status matching the user's active role,
    // not just customerAccountStatus. A provider visiting the
    // customer portal should be gated by providerAccountStatus.
    const roleAccountStatus =
      role === 'provider' ? providerAccountStatus : customerAccountStatus;

    return allowedStatuses.includes(roleAccountStatus);
  }, [
    isAuthenticated,
    hasValidAccessRole,
    role,
    customerAccountStatus,
    providerAccountStatus,
  ]);

  const value = useMemo(
    () => ({
      user: user ?? null,
      loading: isLoading,
      error: isError && error ? (error as Error).message : null,
      isVerifyUser,
      role,
      userId,
      subscriptionPlan,
      refreshUser: () => {
        refetch();
      },
      isAuthenticated,
      needsPhone,
      userAccounts,
      customerAccount,
      customerAccountStatus,
      customerAccountId,
      adminAccountStatus,
      providerAccountStatus,
      hasPermissions,
      profilePicture,
      firstName,
      lastName,
      businessIds,
      activeBusinessId,
      businesses,
      isBusinessOwned,
    }),
    [
      user,
      isLoading,
      isError,
      error,
      refetch,
      role,
      userId,
      subscriptionPlan,
      isVerifyUser,
      needsPhone,
      userAccounts,
      customerAccount,
      adminAccountStatus,
      customerAccountStatus,
      customerAccountId,
      providerAccountStatus,
      hasPermissions,
      profilePicture,
      firstName,
      lastName,
      businessIds,
      activeBusinessId,
      businesses,
      isBusinessOwned,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
